import { IJNativeEvent } from "../JNative";
import { IRectangle } from "./Rectangle";
import { NAMESPACE } from "./_Namespace";

export enum InputTypeEnum
{
    mouse = "mouse",
    touch = "touch",
}

export interface IPoint
{
    x: number;
    y: number;

    add(point: IPoint): void;
    clonePoint(): IPoint;
    distanceTo(point: IPoint): number;
    equals(point: IPoint): boolean;
    headingTo(point: IPoint): number;
    toCoords(): { x: number, y: number };
    withinBounds(rect: IRectangle): boolean;
}

export class Point implements IPoint
{
    // #region Static

    public static classNamespace = NAMESPACE + "Point";
    public static className = "Point";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Public Methods

    public static fromCoord(coord: { x: number, y: number })
    {
        return new Point(coord.x, coord.y);
    }
    public static fromEvent(evt: IJNativeEvent, inputType?: InputTypeEnum)
    {
        if (!inputType)
            inputType = evt.originalEvent instanceof TouchEvent ? InputTypeEnum.touch : InputTypeEnum.mouse;

        switch (inputType)
        {
            case InputTypeEnum.mouse:
                return new Point((evt.originalEvent as MouseEvent).pageX, (evt.originalEvent as MouseEvent).pageY);
            case InputTypeEnum.touch:
                {
                    const
                        input = (evt.originalEvent as TouchEvent).touches[0] || (evt.originalEvent as TouchEvent).changedTouches[0];

                    return new Point(input.pageX, input.pageY);
                }
        }

        throw new Error("Unable to get Point from event");
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public x: number;
    public y: number;

    // #endregion

    // #region Constructor

    constructor(x: number, y: number)
    {
        this.x = x;
        this.y = y;
    }

    // #endregion

    // #region Public Methods

    public add(point: { x: number; y: number; })
    {
        return new Point(this.x + point.x, this.y + point.y);
    }
    public clonePoint()
    {
        return new Point(this.x, this.y);
    }
    public distanceTo(point: { x: number; y: number; })
    {
        return Math.sqrt(Math.pow(point.x - this.x, 2) + Math.pow(point.y - this.y, 2));
    }
    public equals(point: { x: number; y: number; })
    {
        return (
            this.x === point.x
            && this.y === point.y
        );
    }
    public headingTo(point: { x: number; y: number; })
    {
        const
            delta = {
                x: point.x - this.x,
                y: this.y - point.y
            },
            theta = Math.atan2(delta.x, delta.y);

        let
            degrees = theta * 180 / Math.PI;

        if (degrees < 0)
            degrees = 360 + degrees;

        return degrees;
    }
    public toCoords()
    {
        return {
            x: this.x,
            y: this.y
        };
    }
    public withinBounds(rect: IRectangle)
    {
        return (
            this.x >= rect.x1
            && this.x <= rect.x2
            && this.y >= rect.y1
            && this.y <= rect.y2
        );
    }

    // #endregion

    // #endregion
}

